import * as React from "react";
import {
    ClientInquiryMethod,
    ClientStatus,
    IClient,
    ReferralMethods,
    RequestedServices,
    STATES,
    UserSex,
} from "intake_shared/src/types";
import {
    TSaveClient,
    TUpdateClient,
    TUpdateClientByStatus,
    TUpdateMultipleClientFieldsByStatus,
} from "../../../clientUiTypes";
import { useToasts } from "react-toast-notifications";
import moment from "../../../../../../utils/app_moment";
import { Input } from "../../../../bits/Input/Input";
import PhoneInput from "../../../../bits/PhoneInput/PhoneInput";
import InsuranceForm from "../InsuranceForm/InsuranceForm";
import { Select } from "../../../../bits/Select/Select";
import { isClientMinor } from "../../../../../../utils/age";
import { AclContext } from "../../../../../../contexts/AclContext";
import store from "../../../../../../store";
import { Button } from "../../../../bits/Button/Button";
import CancelIcon from "../../../../icons/Cancel";
import SaveCheckIcon from "../../../../icons/SaveCheck";
import { formatLegacyPhoneNumber, toTitleCase } from "../../../../../../utils/utils";
import { DatePicker } from "../../../../bits/DatePicker/DatePicker";
import { Card } from "../../../../bits/Card/Card";
import InsuranceCardUpload from "../../insurance/InsuranceCardUpload/InsuranceCardUpload";
import GuardianDetailsFields from "../../GuardianDetailsFields/GuardianDetailsFields";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface InitialInquiryFormProps {
    client: IClient;
    onSaveClient: TSaveClient;
    updateClient: TUpdateClient;
    updateClientByStatus: TUpdateClientByStatus;
    updateMultipleClientFieldsByStatus: TUpdateMultipleClientFieldsByStatus;
}

const getClientStateFieldSelectOptions = () => {
    return STATES.map((state) => {
        return {
            label: state.name,
            value: state.abbrv,
        };
    });
};

const getConsentToSmsOptions = ["Yes", "No"].map((item) => ({
    label: item,
    value: item,
}));

const getReferralMethodOptions = Object.values(ReferralMethods)
    .sort((a, b) => a.localeCompare(b))
    .map((item) => ({
        label: item,
        value: item,
    }));

const getSexOptions = Object.keys(UserSex).map((item) => ({
    label: toTitleCase(item),
    value: UserSex[item],
}));

const getInquiryMethodOptions = Object.values(ClientInquiryMethod).map((item) => ({
    label: toTitleCase(item),
    value: item.toUpperCase(),
}));

const getRequestedServicesOptions = Object.values(RequestedServices).map((item) => ({
    label: item,
    value: item,
}));

export default function InitialInquiryForm({
    client,
    onSaveClient,
    updateClient,
    updateClientByStatus,
    updateMultipleClientFieldsByStatus,
}: InitialInquiryFormProps) {
    const { addToast } = useToasts();
    const momentDob = client?.date_of_birth ? moment(client.date_of_birth) : null;
    const [otherReferral, setOtherReferral] = React.useState("");
    const [showOtherInput, setShowOtherInput] = React.useState(false);
    const [showSaveButton, setShowSaveButton] = React.useState(false);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;

    React.useEffect(() => {
        if (
            client.referred_by &&
            !Object.values(ReferralMethods).some(
                (method) => method.toUpperCase() === client.referred_by.toUpperCase()
            )
        ) {
            setShowOtherInput(true);
            setOtherReferral(client.referred_by);
        } else {
            setShowOtherInput(false);
        }
    }, [client.referred_by]);

    const handleReferralChange = (value) => {
        if (value === ReferralMethods.OTHER) {
            setShowOtherInput(true);
            setOtherReferral("");
            setShowSaveButton(true);
        } else {
            setShowOtherInput(false);
            setShowSaveButton(false);
            updateClient("referred_by", value);
        }
    };

    const handleSaveOtherReferral = async () => {
        if (otherReferral.trim() !== "") {
            await store.updateClientData({
                client_id: client.id,
                changes: { referred_by: otherReferral },
                last_updated_by: loggedInUserEmail as string,
            });
            setShowSaveButton(false);
            addToast("Referral method updated", {
                appearance: "success",
            });
        }
    };

    const handleCancelOtherReferral = () => {
        setShowOtherInput(false);
        setOtherReferral("");
        updateClient("referred_by", "");
    };

    const isMinor = isClientMinor(client?.date_of_birth);

    return (
        <>
            <form
                onSubmit={async (e) => {
                    e.preventDefault();
                }}
                className="v2-form"
            >
                <Card>
                    <p className="sectionTitle">Personal Information</p>
                    <div className="v2-form">
                        <div className="gridThree">
                            <Input
                                label="First name"
                                value={client.first_name}
                                onChange={(e) => {
                                    updateClient("first_name", e.target.value);
                                }}
                            />
                            <Input
                                label="Last name"
                                value={client.last_name}
                                onChange={(e) => {
                                    updateClient("last_name", e.target.value);
                                }}
                            />
                            <Input
                                label="Preferred name"
                                value={client.preferred_name}
                                onChange={(e) => {
                                    updateClient("preferred_name", e.target.value);
                                }}
                            />
                        </div>
                        <div className="gridThree">
                            <Input
                                type="email"
                                label="Email Address"
                                isRequired
                                value={client.email}
                                onChange={(e) => {
                                    updateClient("email", e.target.value);
                                }}
                            />
                            <PhoneInput
                                placeholder="Phone"
                                label="Phone"
                                value={formatLegacyPhoneNumber(
                                    client.phone_no || ""
                                )}
                                onChange={(value) => {
                                    updateClient("phone_no", value || "");
                                }}
                            />

                            <DatePicker
                                isRequired
                                dateFormat={"MM/dd/yyyy"}
                                selected={momentDob?.toDate()}
                                onChange={(date) => {
                                    updateClient("date_of_birth", date as Date);
                                }}
                                placeholderText="Date of birth"
                                label="Date of birth"
                            />
                        </div>
                        <div className="gridThree">
                            <Select
                                label="Consent to SMS"
                                onChange={(val) =>
                                    updateClient(
                                        "consent_to_sms",
                                        (val as Option).value
                                    )
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getConsentToSmsOptions,
                                ]}
                                defaultValue={
                                    client.consent_to_sms
                                        ? getConsentToSmsOptions.find(
                                              (option) =>
                                                  option.value ===
                                                  client.consent_to_sms
                                          )
                                        : ""
                                }
                            />
                            <Input
                                label="Pronouns"
                                value={client.pronouns}
                                onChange={(e) => {
                                    updateClient("pronouns", e.target.value);
                                }}
                            />
                            {!showOtherInput ? (
                                <Select
                                    label="Referral method"
                                    onChange={(val) =>
                                        handleReferralChange((val as Option).value)
                                    }
                                    options={[
                                        { label: "Select option", value: "" },
                                        ...getReferralMethodOptions,
                                    ]}
                                    defaultValue={getReferralMethodOptions.find(
                                        (method) =>
                                            method.value === client.referred_by
                                    )}
                                />
                            ) : (
                                <div className="flexReferral">
                                    <Input
                                        label="Referred by"
                                        value={otherReferral}
                                        onChange={(e) => {
                                            setOtherReferral(
                                                e.target.value?.toUpperCase()
                                            );
                                            updateClient(
                                                "referred_by",
                                                e.target.value
                                            );
                                        }}
                                    />
                                    <div className="referralControls">
                                        {showSaveButton && (
                                            <div>
                                                <Button
                                                    onClick={handleSaveOtherReferral}
                                                    disabled={
                                                        otherReferral.trim() === ""
                                                    }
                                                    variant="normal"
                                                    className="px-0"
                                                >
                                                    <SaveCheckIcon />
                                                </Button>
                                            </div>
                                        )}
                                        <Button
                                            onClick={handleCancelOtherReferral}
                                            variant="normal"
                                            className="px-0"
                                        >
                                            <CancelIcon />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="gridThree">
                            <Select
                                label="Sex"
                                onChange={(val) =>
                                    updateClient("sex", (val as Option).value)
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getSexOptions,
                                ]}
                                defaultValue={getSexOptions.find(
                                    (sex) => sex.value === client?.sex
                                )}
                            />
                            <Input
                                label="Address line"
                                value={client.address}
                                onChange={(e) => {
                                    updateClient("address", e.target.value);
                                }}
                            />
                            <Select
                                id="state"
                                label="State"
                                isSearchable
                                onChange={(val) =>
                                    updateClient("state", (val as Option).value)
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getClientStateFieldSelectOptions(),
                                ]}
                                defaultValue={getClientStateFieldSelectOptions().find(
                                    (state) => state.value === client.state
                                )}
                            />
                        </div>
                        <div className="gridThree">
                            <Input
                                label="City"
                                value={client.city}
                                onChange={(e) => {
                                    updateClient("city", e.target.value);
                                }}
                            />
                            <Input
                                label="Zip code"
                                value={client.zip_code}
                                onChange={(e) => {
                                    updateClient("zip_code", e.target.value);
                                }}
                            />
                            <Select
                                label="Inquiry method"
                                onChange={(val) =>
                                    updateClient(
                                        "inquiry_method",
                                        (val as Option).value.toUpperCase()
                                    )
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getInquiryMethodOptions,
                                ]}
                                defaultValue={getInquiryMethodOptions.find(
                                    (method) =>
                                        method.value ===
                                        client?.inquiry_method?.toUpperCase()
                                )}
                            />
                            <Select
                                label="Requested service"
                                onChange={(val) =>
                                    updateClient(
                                        "requested_service",
                                        (val as Option).value
                                    )
                                }
                                options={[
                                    { label: "Select option", value: "" },
                                    ...getRequestedServicesOptions,
                                ]}
                                defaultValue={getRequestedServicesOptions.find(
                                    (service) =>
                                        service.value === client?.requested_service
                                )}
                            />
                        </div>
                        <InsuranceForm
                            client={client}
                            updateClient={updateClient}
                            updateMultipleClientFieldsByStatus={
                                updateMultipleClientFieldsByStatus
                            }
                        />
                    </div>
                </Card>
                {isMinor && (
                    <GuardianDetailsFields
                        client={client}
                        updateClient={updateClient}
                    />
                )}
            </form>
            {client.status === ClientStatus.INS_FOLLOWUP && (
                <InsuranceCardUpload
                    client={client}
                    updateClientByStatus={updateClientByStatus}
                />
            )}
        </>
    );
}
